// src/components/exam-builder/WordOrderingModal.jsx
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

function splitTokens(s = "") {
  return s
    .split("|")
    .map((x) => x.trim())
    .filter(Boolean);
}

function splitAnswers(multiline = "") {
  return multiline
    .split("\n")
    .map((line) => splitTokens(line))
    .filter((arr) => arr.length > 0);
}

// ریپلیس ساده‌ی [] با Underline برای پریویو
function renderBlanksPreview(text = "") {
  if (!text) return "";
  return text.replace(/\[\s*\]/g, "_____");
}

// فرم خالی
const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  text_fa: "",
  text_en: "",
  tokens: "",
  answers_multiline: "",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// تبدیل آیتم سرور → فرم
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem = item.stem || {};
  const body = item.body || {};
  const text = body.text || {};
  const tokensArr = Array.isArray(body.tokens) ? body.tokens : [];
  const answers = Array.isArray(body.answers) ? body.answers : [];
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa: stem.fa || "",
    stem_en: stem.en || "",
    text_fa: text.fa || "",
    text_en: text.en || "",
    tokens: tokensArr.join(" | "),
    answers_multiline: answers
      .map((ansLine) => (Array.isArray(ansLine) ? ansLine.join(" | ") : ""))
      .filter(Boolean)
      .join("\n"),
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : item.tags || "",
    explanation_fa,
    explanation_en,
  };
};

export default function WordOrderingModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  React.useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const tokensArr = useMemo(() => splitTokens(form.tokens), [form.tokens]);
  const answersArr = useMemo(
    () => splitAnswers(form.answers_multiline),
    [form.answers_multiline]
  );

  const validate = () => {
    const hasFaBlank = /\[\s*\]/.test(form.text_fa || "");
    const hasEnBlank = /\[\s*\]/.test(form.text_en || "");
    if (!hasFaBlank && !hasEnBlank) {
      toast.error(t("word_ordering.toasts.no_placeholders_in_text"));
      return false;
    }

    if (tokensArr.length === 0) {
      toast.error(t("word_ordering.toasts.tokens_required"));
      return false;
    }
    if (answersArr.length === 0) {
      toast.error(t("word_ordering.toasts.answer_required"));
      return false;
    }

    // طول پاسخ‌ها = تعداد توکن‌ها
    const badLen = answersArr.find((ans) => ans.length !== tokensArr.length);
    if (badLen) {
      toast.error(t("word_ordering.toasts.answer_length_mismatch"));
      return false;
    }

    // پاسخ فقط شامل توکن‌های داده‌شده باشد
    const tokSet = new Set(tokensArr);
    const badToken = answersArr.find((ans) => ans.some((w) => !tokSet.has(w)));
    if (badToken) {
      toast.error(t("word_ordering.toasts.answer_contains_unknown"));
      return false;
    }

    // بررسی چندمجموعه‌ای
    const freq = (arr) =>
      arr.reduce((m, x) => ((m[x] = (m[x] || 0) + 1), m), {});
    const tokensFreq = freq(tokensArr);
    const badMultiset = answersArr.find((ans) => {
      const f = freq(ans);
      const keys = new Set([...Object.keys(f), ...Object.keys(tokensFreq)]);
      for (const k of keys) {
        if ((f[k] || 0) !== (tokensFreq[k] || 0)) return true;
      }
      return false;
    });
    if (badMultiset) {
      toast.error(t("word_ordering.toasts.answer_multiset_mismatch"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        kind: "sentence_reconstruction",
        explanation: {
          fa: form.explanation_fa || undefined,
          en: form.explanation_en || undefined,
        },
        stem: {
          fa: form.stem_fa || undefined,
          en: form.stem_en || undefined,
        },
        body: {
          text: {
            fa: form.text_fa || undefined,
            en: form.text_en || undefined,
          },
          tokens: tokensArr,
          answers: answersArr, // هر خط یک پاسخ
        },
        scoring: { mode: "exact" },
        points: Number(form.points) || 1,
        status: form.status || "draft",
        tags: form.tags
          ? form.tags
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };

      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t(
              "word_ordering.toasts.updated",
              "سؤال بازچینش کلمات به‌روزرسانی شد"
            )
          : t("word_ordering.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  if (!isOpen) return null;

  const previewFa = renderBlanksPreview(form.text_fa);
  const previewEn = renderBlanksPreview(form.text_en);

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Stem (اختیاری) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.stem_fa")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(c) => setForm((f) => ({ ...f, stem_fa: c }))}
              placeholder={t("word_ordering.stem_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.stem_en")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(c) => setForm((f) => ({ ...f, stem_en: c }))}
              placeholder={t("word_ordering.stem_en_ph")}
            />
          </div>
        </div>

        {/* متن سؤال با جای‌خالی‌ها */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.text_fa")}
            </label>
            <CustomEditor
              value={form.text_fa}
              onEditorChange={(c) => setForm((f) => ({ ...f, text_fa: c }))}
              placeholder={t("word_ordering.text_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.text_en")}
            </label>
            <CustomEditor
              value={form.text_en}
              onEditorChange={(c) => setForm((f) => ({ ...f, text_en: c }))}
              placeholder={t("word_ordering.text_en_ph")}
            />
          </div>
        </div>

        {/* پریویو متن با Underline */}
        {(previewFa || previewEn) && (
          <div className="rounded-lg border bg-gray-50 p-3">
            <div className="text-xs text-gray-500 mb-2">
              {t("word_ordering.preview_title")}
            </div>
            {previewFa && (
              <p dir="rtl" className="mb-1">
                {previewFa}
              </p>
            )}
            {previewEn && (
              <p dir="ltr" className="mb-1">
                {previewEn}
              </p>
            )}
          </div>
        )}

        {/* Tokens */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("word_ordering.tokens")}
          </label>
          <input
            type="text"
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("word_ordering.tokens_ph")}
            value={form.tokens}
            onChange={(e) => setForm((f) => ({ ...f, tokens: e.target.value }))}
          />
          {tokensArr.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tokensArr.map((tk, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded-full bg-gray-100 border text-xs"
                >
                  {tk}
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {t("word_ordering.tokens_hint")}
          </p>
        </div>

        {/* Answers (multi-line) */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("word_ordering.answers")}
          </label>
          <textarea
            rows={5}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("word_ordering.answers_ph")}
            value={form.answers_multiline}
            onChange={(e) =>
              setForm((f) => ({ ...f, answers_multiline: e.target.value }))
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            {t("word_ordering.answers_hint")}
          </p>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.points")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={(e) =>
                setForm((f) => ({ ...f, points: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.status")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("word_ordering.tags")}
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />

        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          {isEdit ? t("common.update") : t("common.create")}
        </button>
      </form>
    </div>
  );
}
